// Generated from shared source. Run work/build-uploader-shared.cjs to refresh.
import { pricing, calculatePrint, calculateMounting, money } from './ReviewedPricingEngine';
export const pricingPolicyVersion = '2026-09-16-paper-size-no-auto-discounts-explicit-dibond-paper';
// Let the release gateway identify a reviewed print/mount page after rollback.
// Framing requests retain their existing headers and pricing path.
export function pricingRequestHeaders(kind) {
    return ['print', 'mounting'].includes(kind) ? { 'X-Tricera-Pricing-Version': pricingPolicyVersion } : {};
}
const normalized = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
export function paperIdFor(value) {
    var _a;
    const name = normalized(typeof value === 'object' ? value === null || value === void 0 ? void 0 : value.Papertype : value);
    if (name === 'epson legacy baryta')
        return 'epson-premium-glossy';
    return (_a = pricing.print.papers.find(p => p.id === name || normalized(p.name) === name)) === null || _a === void 0 ? void 0 : _a.id;
}
export function mountingChoice(value) {
    const choices = {
        'Archival Foam Board': ['foam-board', 'framing'],
        'UltraBoard (3/16")': ['ultra-3-16', 'framing'],
        'UltraBoard (1/2")': ['ultra-1-2', 'framing'],
        'Dibond Float Mount': ['dibond', 'float'],
        'Dibond — Laminated': ['dibond', 'laminate'],
        'Dibond — Prepared for Framing': ['dibond', 'framing'],
        'Alupanel': ['dibond', 'float'],
    };
    return choices[value];
}
// Crop responses contain pixel dimensions, from which the legacy UI derives a
// rounded one-decimal size. Keep the customer's accepted dimensions separately
// so that accepting a crop cannot change its billable size or eligibility.
export function rememberRequestedImageSize(session, imageId, dimensions) {
    const width = Number(dimensions === null || dimensions === void 0 ? void 0 : dimensions.width), height = Number(dimensions === null || dimensions === void 0 ? void 0 : dimensions.height), measurement = dimensions === null || dimensions === void 0 ? void 0 : dimensions.measurement;
    if (![width, height].every(v => Number.isFinite(v) && v > 0) || !['inch', 'cm'].includes(measurement))
        return session;
    return { ...session, data: session.data.map(item => item._id === imageId
            ? { ...item, config: { ...item.config, triceraRequestedSize: { width, height, measurement } } }
            : item) };
}
export function requestedImageDimensions(image, measurement) {
    const size = image === null || image === void 0 ? void 0 : image.triceraRequestedSize;
    if (!size || !['inch', 'cm'].includes(size.measurement) || !['inch', 'cm'].includes(measurement))
        return null;
    if (![size.width, size.height].every(v => Number.isFinite(Number(v)) && Number(v) > 0))
        return null;
    const factor = size.measurement === measurement ? 1 : measurement === 'cm' ? 2.54 : 1 / 2.54;
    return { width: Number((Number(size.width) * factor).toFixed(10)), height: Number((Number(size.height) * factor).toFixed(10)) };
}
export const formatQuotedDimension = value => Number(Number(value).toFixed(10));
export function paperDimensions({ width, height, measurement, borders = {} }) {
    if (!['inch', 'cm'].includes(measurement))
        throw new Error('Select inches or centimetres.');
    const w = Number(width), h = Number(height);
    if (![w, h].every(v => Number.isFinite(v) && v > 0))
        throw new Error('Enter positive image dimensions.');
    const b = Object.fromEntries(['Left', 'Right', 'Top', 'Bottom'].map(key => { var _a; return [key, Number((_a = borders[key]) !== null && _a !== void 0 ? _a : 0)]; }));
    if (!Object.values(b).every(v => Number.isFinite(v) && v >= 0))
        throw new Error('Borders must be finite, non-negative numbers.');
    const divisor = measurement === 'cm' ? 2.54 : 1;
    // Bill the outside paper size, not the occupied image area. No hidden allowance.
    const paperWidth = Number(((w + b.Left + b.Right) / divisor).toFixed(10));
    const paperHeight = Number(((h + b.Top + b.Bottom) / divisor).toFixed(10));
    return { paperWidth, paperHeight };
}
export function quoteUploader(input) {
    try {
        if (!input || !['print', 'mounting'].includes(input.kind))
            throw new Error('Select print or mounting.');
        const quantity = Number(input.quantity);
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100)
            throw new Error('Enter a whole quantity from 1 to 100. For larger orders, contact us.');
        const { paperWidth, paperHeight } = paperDimensions(input);
        let paperId = paperIdFor(input.paperId || input.paper);
        let result, unitPrice;
        if (input.kind === 'mounting') {
            const choice = mountingChoice(input.mounting);
            if (!choice)
                throw new Error('Select a configured mounting option.');
            // Validate the customer's selection without silently replacing their paper.
            // Collect compatibility and size failures together so neither hides the other.
            const errors = [];
            if (choice[0] === 'dibond' && paperId !== pricing.mounting.requiredDibondPaper) {
                const required = pricing.print.papers.find(p => p.id === pricing.mounting.requiredDibondPaper);
                errors.push(`Dibond mounting requires ${required.name}. Please change your paper selection.`);
            }
            result = calculateMounting(paperWidth, paperHeight, choice[0], paperId, choice[1]);
            if (result.error || result.printQuoteReason)
                errors.push(result.error || result.printQuoteReason);
            if (errors.length)
                return { error: errors.join(' '), errors };
            unitPrice = result.total;
        }
        else {
            if (!paperId)
                throw new Error('Select a configured paper.');
            result = calculatePrint(paperWidth, paperHeight, paperId, 1);
            if (result.error)
                throw new Error(result.error);
            unitPrice = result.displayedUnit;
        }
        if (!Number.isFinite(unitPrice) || unitPrice <= 0)
            throw new Error('This size needs a custom quote.');
        // Keep the original Print Price / Mount Price display. Allocate the final
        // rounding remainder to mounting so the two billed lines sum to the unit
        // price; never add separately rounded components to calculate checkout.
        const printPrice = input.kind === 'mounting' ? money(result.paperCost) : unitPrice;
        const mountPrice = input.kind === 'mounting' ? unitPrice - printPrice : 0;
        return { policyVersion: pricingPolicyVersion, paperWidth, paperHeight, paperId, paperName: pricing.print.papers.find(p => p.id === paperId).name, unitPrice, printPrice, mountPrice, quantity, total: unitPrice * quantity, discount: 0, breakdown: result };
    }
    catch (error) {
        return { error: error.message };
    }
}
// Framing is deliberately outside this adapter and keeps its existing controller
// path. Other supported products must provide structured, valid pricing inputs.
export function priceProductInput(productInput, quantity) {
    if (productInput.product_type === 'Framing')
        return null;
    const kind = productInput.product_type === 'Print Only' ? 'print' : productInput.product_type === 'Mounting' ? 'mounting' : null;
    if (!kind)
        throw new Error('Unsupported product type.');
    const input = productInput.triceraPricingInput;
    if (!input)
        throw new Error('The pricing calculator has been updated. Please refresh this page, review your print settings, and try again.');
    if (input.kind !== kind)
        throw new Error('Missing or inconsistent pricing inputs.');
    const quote = quoteUploader({ ...input, quantity });
    if (quote.error)
        throw new Error(quote.error);
    return quote;
}
